/**
 * Popups section — promo modal behaviour adapted from popup-build package.
 */
(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 750;
  const FOCUSABLE =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  const selectors = {
    promoPopup: '[data-promo-popup]',
    promoPopupInner: '[data-promo-popup-inner]',
    close: '[data-close-modal]',
    modalUnderlay: '[data-modal-underlay]',
    modalBody: '[data-modal-body]',
    delayAttribute: 'data-popup-delay',
    cookieNameAttribute: 'data-cookie-name',
    showOnceAttribute: 'data-show-once',
    dataTargetReferrer: 'data-target-referrer',
    sectionType: 'data-section-type',
    sectionId: 'data-section-id',
  };

  const classes = {
    visible: 'is-visible',
    hidden: 'pb-promo-popup__overlay--hidden',
    selected: 'selected',
    hasBlockSelected: 'has-block-selected',
    mobile: 'mobile',
    desktop: 'desktop',
  };

  const sectionInstances = {};

  const fadeIn = (el, callback = null) => {
    el.classList.remove(classes.hidden);
    el.style.opacity = 0;
    el.style.display = 'block';
    let flag = true;

    (function fade() {
      var val = parseFloat(el.style.opacity);
      if (!((val += 0.1) > 1)) {
        el.style.opacity = val;
        requestAnimationFrame(fade);
      }

      if (parseInt(val, 10) === 1) {
        el.classList.add(classes.visible);
        if (flag && typeof callback === 'function') {
          flag = false;
          callback();
        }
      }
    })();
  };

  const fadeOut = (el, callback = null) => {
    el.classList.remove(classes.visible);
    el.style.opacity = 1;

    (function fade() {
      if ((el.style.opacity -= 0.1) < 0) {
        el.style.display = 'none';
      } else {
        requestAnimationFrame(fade);
      }

      if (parseFloat(el.style.opacity) === 0 && typeof callback === 'function') {
        callback();
      }
    })();
  };

  const trapFocusHandlers = {};

  function focusable(container) {
    return Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE));
  }

  function removeTrapFocus() {
    document.removeEventListener('focusin', trapFocusHandlers.focusin);
    document.removeEventListener('focusout', trapFocusHandlers.focusout);
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  }

  function trapFocus(container) {
    const elements = focusable(container);
    const first = elements[0];
    const last = elements[elements.length - 1];

    removeTrapFocus();

    trapFocusHandlers.focusin = function (event) {
      if (
        container !== event.target &&
        !container.contains(event.target) &&
        first &&
        first === event.target
      ) {
        first.focus();
      }

      if (
        event.target !== container &&
        event.target !== last &&
        event.target !== first
      ) {
        return;
      }

      document.addEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = function () {
      document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = function (event) {
      const tabKey = window.theme?.keyboardKeys?.TAB ?? 9;
      if (event.keyCode !== tabKey) return;

      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }

      if (
        (event.target === container || event.target === first) &&
        event.shiftKey
      ) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);

    if (first) first.focus();
  }

  function lockScroll(detail) {
    document.documentElement.setAttribute('data-scroll-locked', '');
    document.body.classList.add('overflow-hidden');
    document.dispatchEvent(
      new CustomEvent('theme:scroll:lock', { bubbles: true, detail })
    );
  }

  function unlockScroll() {
    document.documentElement.removeAttribute('data-scroll-locked');
    document.body.classList.remove('overflow-hidden');
    document.dispatchEvent(
      new CustomEvent('theme:scroll:unlock', { bubbles: true })
    );
  }

  class PopupCookie {
    constructor(name, value, expiresDays) {
      this.configuration = {
        expires: expiresDays
          ? new Date(
              Date.now() + expiresDays * 24 * 60 * 60 * 1000
            ).toUTCString()
          : null,
        path: '/',
        domain: window.location.hostname,
      };
      this.name = name;
      this.value = value;
    }

    write() {
      const expiresPart = this.configuration.expires
        ? `; expires=${this.configuration.expires}`
        : '';
      document.cookie = `${this.name}=${this.value}${expiresPart}; path=${this.configuration.path}; domain=${this.configuration.domain}`;
    }

    read() {
      if (
        document.cookie.indexOf('; ') !== -1 &&
        document.cookie.split('; ').find((row) => row.startsWith(this.name))
      ) {
        return document.cookie
          .split('; ')
          .find((row) => row.startsWith(this.name))
          .split('=')[1];
      }

      return false;
    }
  }

  class DelayShow {
    constructor(holder, element, callback = null) {
      this.element = element;
      this.delay = holder.getAttribute(selectors.delayAttribute);
      this.callback = callback;
      this.showPopupOnScrollEvent = () => this.showPopupOnScroll();

      if (this.delay === 'always') {
        this.always();
      }

      if (this.delay && this.delay.includes('delayed')) {
        const seconds = this.delay.includes('_')
          ? parseInt(this.delay.split('_')[1], 10)
          : 10;
        this.delayed(seconds);
      }

      if (this.delay === 'bottom') {
        this.bottom();
      }

      if (this.delay === 'idle') {
        this.idle();
      }
    }

    always() {
      fadeIn(this.element, this.callback);
    }

    delayed(seconds = 10) {
      setTimeout(() => {
        fadeIn(this.element, this.callback);
      }, seconds * 1000);
    }

    idle() {
      let timer = 0;
      const idleTime = 60000;
      const documentEvents = [
        'mousemove',
        'mousedown',
        'click',
        'touchmove',
        'touchstart',
        'touchend',
        'keydown',
        'keypress',
      ];
      const windowEvents = ['load', 'resize', 'scroll'];

      const resetTimer = () => {
        if (timer) clearTimeout(timer);

        documentEvents.forEach((eventType) => {
          document.removeEventListener(eventType, resetTimer);
        });

        windowEvents.forEach((eventType) => {
          window.removeEventListener(eventType, resetTimer);
        });

        startTimer();
      };

      const startTimer = () => {
        timer = setTimeout(() => {
          timer = 0;
          fadeIn(this.element, this.callback);
        }, idleTime);

        documentEvents.forEach((eventType) => {
          document.addEventListener(eventType, resetTimer);
        });

        windowEvents.forEach((eventType) => {
          window.addEventListener(eventType, resetTimer);
        });
      };

      startTimer();
    }

    bottom() {
      window.addEventListener('scroll', this.showPopupOnScrollEvent, {
        passive: true,
      });
      document.addEventListener('theme:scroll', this.showPopupOnScrollEvent);
    }

    showPopupOnScroll() {
      if (window.scrollY + window.innerHeight >= document.body.clientHeight) {
        fadeIn(this.element, this.callback);
        window.removeEventListener('scroll', this.showPopupOnScrollEvent);
        document.removeEventListener('theme:scroll', this.showPopupOnScrollEvent);
      }
    }

    onUnload() {
      window.removeEventListener('scroll', this.showPopupOnScrollEvent);
      document.removeEventListener('theme:scroll', this.showPopupOnScrollEvent);
    }
  }

  class TargetReferrer {
    constructor(el) {
      this.el = el;
      this.locationPath = location.href;

      if (!this.el.hasAttribute(selectors.dataTargetReferrer)) {
        return;
      }

      this.init();
    }

    init() {
      if (
        this.locationPath.indexOf(
          this.el.getAttribute(selectors.dataTargetReferrer)
        ) === -1 &&
        !window.Shopify.designMode
      ) {
        this.el.parentNode.removeChild(this.el);
      }
    }
  }

  class PromoPopup {
    constructor(el) {
      this.popup = el;
      this.modal = this.popup.querySelector(selectors.promoPopupInner);
      this.modalBody = this.popup.querySelector(selectors.modalBody);
      this.closeButtons = this.popup.querySelectorAll(selectors.close);
      this.underlay = this.popup.querySelector(selectors.modalUnderlay);
      this.showOnce =
        this.popup.getAttribute(selectors.showOnceAttribute) !== 'false';
      this.cookie = new PopupCookie(
        this.popup.getAttribute(selectors.cookieNameAttribute),
        'user_has_closed',
        365
      );
      this.isTargeted = new TargetReferrer(this.popup);

      this.init();
    }

    init() {
      const cookieExists = this.cookie.read() !== false;
      const targetMobile = this.popup.classList.contains(classes.mobile);
      const targetDesktop = this.popup.classList.contains(classes.desktop);
      let targetMatches = true;

      if (
        (targetMobile && window.innerWidth >= MOBILE_BREAKPOINT) ||
        (targetDesktop && window.innerWidth < MOBILE_BREAKPOINT)
      ) {
        targetMatches = false;
      }

      if (!targetMatches) {
        this.scrollUnlock();
        return;
      }

      if (!this.showOnce || !cookieExists || window.Shopify.designMode) {
        if (!window.Shopify.designMode) {
          new DelayShow(this.popup, this.modal, () => this.scrollLock());
        }

        this.initClosers();
      }
    }

    initClosers() {
      this.closeButtons.forEach((button) => {
        button.addEventListener('click', this.closeModal.bind(this));
      });
      if (this.underlay) {
        this.underlay.addEventListener('click', this.closeModal.bind(this));
      }

      this.popup.addEventListener('keyup', (event) => {
        if (event.code?.toUpperCase() === 'ESCAPE') {
          this.closeModal(event);
        }
      });
    }

    closeModal(e) {
      e.preventDefault();
      fadeOut(this.modal);
      if (this.showOnce) {
        this.cookie.write();
      }
      this.scrollUnlock();
    }

    scrollLock() {
      if (window.getComputedStyle(this.popup).display !== 'none') {
        const dialog = this.popup.querySelector('[role="dialog"]');
        if (dialog) trapFocus(dialog);
        lockScroll(this.modalBody);
      }
    }

    scrollUnlock() {
      removeTrapFocus();
      unlockScroll();
    }

    onBlockSelect(evt) {
      if (this.popup.contains(evt.target)) {
        fadeIn(this.modal, () => this.scrollLock());
        this.popup.classList.add(classes.selected);
        this.popup.parentNode.classList.add(classes.hasBlockSelected);
      }
    }

    onBlockDeselect(evt) {
      if (this.popup.contains(evt.target)) {
        fadeOut(this.modal);
        this.scrollUnlock();
        this.popup.classList.remove(classes.selected);
        this.popup.parentNode.classList.remove(classes.hasBlockSelected);
      }
    }
  }

  function initPopupsSection(container) {
    const sectionId = container.getAttribute(selectors.sectionId);
    if (!sectionId) return;

    sectionInstances[sectionId] = [];

    container.querySelectorAll(selectors.promoPopup).forEach((el) => {
      sectionInstances[sectionId].push(new PromoPopup(el));
    });
  }

  function handleBlockSelect(sectionId, evt) {
    (sectionInstances[sectionId] || []).forEach((instance) => {
      if (typeof instance.onBlockSelect === 'function') {
        instance.onBlockSelect(evt);
      }
    });
  }

  function handleBlockDeselect(sectionId, evt) {
    (sectionInstances[sectionId] || []).forEach((instance) => {
      if (typeof instance.onBlockDeselect === 'function') {
        instance.onBlockDeselect(evt);
      }
    });
  }

  function boot() {
    document
      .querySelectorAll(`[${selectors.sectionType}="popups"]`)
      .forEach(initPopupsSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector?.(
      `[${selectors.sectionType}="popups"]`
    );
    if (container) initPopupsSection(container);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest(`[${selectors.sectionType}="popups"]`);
    if (section) {
      handleBlockSelect(section.getAttribute(selectors.sectionId), event);
    }
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const section = event.target.closest(`[${selectors.sectionType}="popups"]`);
    if (section) {
      handleBlockDeselect(section.getAttribute(selectors.sectionId), event);
    }
  });
})();
